import { useMemo } from "react";

import { useLocalStorage } from "usehooks-ts";

import { createFNVHashSync } from "../../../components/authentication/utils";

export type GuideLocation = "dashboard" | "sidebar";

export interface OnboardingGuideState {
  guideLocation: GuideLocation;
  remindMeLaterUntil: string | null;
  dismissedExistingPrompts: Record<string, { timestamp: string | null }>;
  caughtUpDismissed: boolean;
  forceReset: boolean;
}

export const DEFAULT_ONBOARDING_GUIDE_STATE: OnboardingGuideState = {
  guideLocation: "dashboard",
  remindMeLaterUntil: null,
  dismissedExistingPrompts: {},
  caughtUpDismissed: false,
  forceReset: false,
};

/**
 * Generate the localStorage key for a user's onboarding guide state.
 * Format: onboarding-guide-{hashedUserId}
 *
 * @param userId - Unique user identifier
 * @returns localStorage key for this user's onboarding guide state
 */
export const getOnboardingGuideStorageKey = (userId: string): string => {
  const hashedId = createFNVHashSync(userId);
  return `onboarding-guide-${hashedId}`;
};

/**
 * Migrate from old localStorage keys to new consolidated storage.
 * Checks for existing `onboarding-${userId}` and `onboarding-remind-later-${userId}` keys
 * and migrates their values to the new consolidated storage format.
 *
 * @param userId - User identifier
 * @returns Migrated state or default state
 */
export const migrateFromLegacyStorage = (
  userId: string
): OnboardingGuideState => {
  const legacyDismissedKey = `onboarding-${userId}`;
  const legacyRemindLaterKey = `onboarding-remind-later-${userId}`;

  const isDismissed = !!window.localStorage.getItem(legacyDismissedKey);

  if (isDismissed) {
    // Previously dismissed users now see guide in sidebar
    return {
      guideLocation: "sidebar",
      remindMeLaterUntil: null,
      dismissedExistingPrompts: {},
      caughtUpDismissed: false,
      forceReset: false,
    };
  }

  const remindLaterData = window.localStorage.getItem(legacyRemindLaterKey);

  if (remindLaterData) {
    try {
      const { timestamp, days } = JSON.parse(remindLaterData);
      const hiddenUntil = new Date(timestamp);
      hiddenUntil.setDate(hiddenUntil.getDate() + days);

      return {
        guideLocation: "sidebar",
        remindMeLaterUntil: hiddenUntil.toISOString(),
        dismissedExistingPrompts: {},
        caughtUpDismissed: false,
        forceReset: false,
      };
    } catch (error) {
      console.warn("Failed to migrate remind me later data", error);
    }
  }

  return DEFAULT_ONBOARDING_GUIDE_STATE;
};

/**
 * Hook to manage onboarding guide state for a specific user with localStorage persistence.
 * State is reactive and persists across page navigations and browser sessions.
 * Each user gets their own isolated guide state based on their hashed user ID.
 *
 * Automatically migrates from legacy localStorage keys on first use.
 *
 * @param userId - Unique identifier for the current user
 * @returns Tuple of [state, setState, removeValue] similar to useState
 *
 * @example
 * ```tsx
 * const [guideState, setGuideState] = useOnboardingGuideStorage(currentUser.id);
 *
 * // Move guide to sidebar
 * setGuideState(prev => guideActions.moveToSidebar(prev));
 *
 * // Check if should show in sidebar
 * const shouldShowInSidebar = guideActions.shouldShowInSidebar(guideState);
 * ```
 */
export const useOnboardingGuideStorage = (userId: string) => {
  const storageKey = useMemo(
    () => getOnboardingGuideStorageKey(userId),
    [userId]
  );

  // Initialize with migration check
  const initialValue = useMemo(() => {
    const existingValue = window.localStorage.getItem(storageKey);

    if (existingValue) {
      try {
        const parsed = JSON.parse(existingValue);
        return { ...DEFAULT_ONBOARDING_GUIDE_STATE, ...parsed };
      } catch {
        return DEFAULT_ONBOARDING_GUIDE_STATE;
      }
    }

    // No existing value, check for migration
    return migrateFromLegacyStorage(userId);
  }, [storageKey, userId]);

  const [state, setState, removeValue] = useLocalStorage<OnboardingGuideState>(
    storageKey,
    initialValue
  );

  return [state, setState, removeValue] as const;
};

/**
 * Utility functions for common onboarding guide state operations
 */
export const guideActions = {
  /**
   * Move guide to sidebar (called when "Remind me later" is clicked)
   */
  moveToSidebar: (
    state: OnboardingGuideState,
    remindMeLaterDays?: number
  ): OnboardingGuideState => {
    if (typeof remindMeLaterDays !== "number" || remindMeLaterDays <= 0) {
      return {
        ...state,
        guideLocation: "sidebar",
        remindMeLaterUntil: null,
      };
    }

    const remindMeLaterUntil = new Date();
    remindMeLaterUntil.setDate(
      remindMeLaterUntil.getDate() + remindMeLaterDays
    );

    return {
      ...state,
      guideLocation: "sidebar",
      remindMeLaterUntil: remindMeLaterUntil.toISOString(),
    };
  },

  /**
   * Move guide to dashboard (typically called when re-enabling from sidebar)
   */
  moveToDashboard: (state: OnboardingGuideState): OnboardingGuideState => ({
    ...state,
    guideLocation: "dashboard",
    remindMeLaterUntil: null,
  }),

  /**
   * Check if guide should show in sidebar
   */
  shouldShowInSidebar: (state: OnboardingGuideState): boolean => {
    return state.guideLocation === "sidebar";
  },

  /**
   * Check if guide should show in dashboard
   */
  shouldShowInDashboard: (state: OnboardingGuideState): boolean => {
    // Check if remind me later period has expired
    if (state.remindMeLaterUntil) {
      const hiddenUntil = new Date(state.remindMeLaterUntil);
      if (new Date() < hiddenUntil) {
        // Still within remind me later period, stay in sidebar
        return false;
      }
    }

    return state.guideLocation === "dashboard";
  },

  dismissPrompt: (
    state: OnboardingGuideState,
    promptUuid: string
  ): OnboardingGuideState => ({
    ...state,
    dismissedExistingPrompts: {
      ...state.dismissedExistingPrompts,
      [promptUuid]: { timestamp: new Date().toISOString() },
    },
  }),

  isPromptDismissed: (
    state: OnboardingGuideState,
    promptUuid: string
  ): boolean => {
    return promptUuid in (state.dismissedExistingPrompts ?? {});
  },

  resetDismissedPrompts: (
    state: OnboardingGuideState
  ): OnboardingGuideState => ({
    ...state,
    dismissedExistingPrompts: {},
  }),

  dismissCaughtUp: (state: OnboardingGuideState): OnboardingGuideState => ({
    ...state,
    caughtUpDismissed: true,
  }),

  resetCaughtUpDismissed: (
    state: OnboardingGuideState
  ): OnboardingGuideState => ({
    ...state,
    caughtUpDismissed: false,
  }),

  /**
   * Flag the guides to refresh from the server. Set by completion sites
   * (e.g. download transactions, income reporting) so a mounted guide can
   * refetch and play the completion animation without a page reload.
   */
  setForceReset: (state: OnboardingGuideState): OnboardingGuideState => ({
    ...state,
    forceReset: true,
  }),

  /**
   * Clear the force-reset flag once the guide has consumed it.
   */
  clearForceReset: (state: OnboardingGuideState): OnboardingGuideState => ({
    ...state,
    forceReset: false,
  }),
};
